/**
 * Runtime Configuration Utility
 *
 * This utility reads configuration values from multiple sources in order of priority:
 * 1. Runtime config injected into HTML (highest priority)
 * 2. Build-time environment variables (fallback)
 *
 * This allows for flexible configuration that can be overridden at container startup
 * without requiring a rebuild of the application.
 */

interface RuntimeConfigMap {
  [key: string]: string | undefined;
}

let runtimeConfigCache: RuntimeConfigMap | null = null;

/**
 * Gets runtime configuration injected into the HTML by the startup script
 * @returns Object containing runtime configuration values
 */
function getRuntimeConfig(): RuntimeConfigMap {
  if (runtimeConfigCache !== null) {
    return runtimeConfigCache;
  }

  try {
    // Try to read the runtime config script tag injected by the startup script
    const runtimeConfigElement = document.getElementById("runtime-config");

    if (runtimeConfigElement && runtimeConfigElement.textContent) {
      const runtimeConfig = JSON.parse(runtimeConfigElement.textContent);
      runtimeConfigCache = runtimeConfig;
      console.log(
        "🚀 Loaded runtime configuration:",
        Object.keys(runtimeConfig).length + " values",
      );
      return runtimeConfig;
    }
  } catch (error) {
    console.warn("⚠️ Failed to parse runtime configuration:", error);
  }

  // Fallback to empty object if no runtime config is available
  runtimeConfigCache = {};
  return runtimeConfigCache;
}

/**
 * Gets a configuration value with runtime override support
 * @param key The environment variable key (e.g., 'VITE_API_BASE_URL')
 * @param fallback Optional fallback value if neither runtime nor build-time value exists
 * @returns The configuration value, preferring runtime over build-time
 */
export function getRuntimeEnvVar(
  key: string,
  fallback?: string,
): string | undefined {
  const runtimeConfig = getRuntimeConfig();

  // First check runtime config (injected at container startup)
  const runtimeValue = runtimeConfig[key];
  if (runtimeValue && runtimeValue.trim() !== "") {
    return runtimeValue;
  }

  // Fallback to build-time environment variable
  const buildTimeValue = import.meta.env[key];
  if (buildTimeValue && buildTimeValue.trim() !== "") {
    return buildTimeValue;
  }

  // Final fallback
  return fallback;
}

/**
 * Gets a boolean configuration value with runtime override support
 * @param key The environment variable key
 * @param fallback Optional fallback value
 * @returns Boolean value, where 'true' string becomes true boolean
 */
export function getRuntimeEnvVarBoolean(
  key: string,
  fallback: boolean = false,
): boolean {
  const value = getRuntimeEnvVar(key);
  if (value === undefined) {
    return fallback;
  }
  return value.toLowerCase() === "true";
}

/**
 * Logs the current configuration state for debugging
 */
export function logConfigurationState(): void {
  const runtimeConfig = getRuntimeConfig();
  const hasRuntimeConfig = Object.keys(runtimeConfig).length > 0;

  console.group("🔧 Configuration State");
  console.log("Runtime config available:", hasRuntimeConfig);

  if (hasRuntimeConfig) {
    console.log("Runtime config keys:", Object.keys(runtimeConfig));

    // Log a few key values for debugging (without sensitive data)
    const debugKeys = [
      "VITE_APP_ENV",
      "VITE_API_BASE_URL",
      "VITE_APP_BASE_URL",
    ];
    debugKeys.forEach((key) => {
      const value = runtimeConfig[key];
      if (value) {
        console.log(`${key}:`, value);
      }
    });
  } else {
    console.log("Using build-time configuration only");
  }

  console.groupEnd();
}
